<template>
  <a-modal
    :open="visible"
    title="📋 功能需求说明"
    :footer="null"
    width="900px"
    @cancel="handleClose"
  >
    <div class="requirement-doc">
      <a-typography>
        <a-typography-title :level="3">AI调用流程出参列表查看</a-typography-title>
        
        <a-divider />
        
        <a-typography-title :level="4">一、需求概述</a-typography-title>
        
        <a-typography-title :level="5">1.1 背景</a-typography-title>
        <a-typography-paragraph>
          在 AI 调用流程中，每个 Worker 都有 <a-typography-text code>output_params</a-typography-text>（出参）定义，
          这些出参与调用系统的参数使用有直接关系。出参的 JSON 结构一旦确定，如果需要修改，
          理论上等同于<a-typography-text strong>接口文档变更</a-typography-text>。
        </a-typography-paragraph>
        <a-typography-paragraph>
          目前系统缺少一个统一视图来查看整个流程下所有 Worker 的出参定义，导致：
          <ul>
            <li>开发联调时需要逐个点击 Worker 查看出参</li>
            <li>接口变更时难以评估影响范围</li>
            <li>缺乏出参结构的版本管理和变更追踪</li>
          </ul>
        </a-typography-paragraph>
        
        <a-typography-title :level="5">1.2 目标</a-typography-title>
        <a-typography-paragraph>
          <ul>
            <li>提供统一的出参列表查看页面，一览当前 AI 调用流程下所有 Worker 的出参定义</li>
            <li>支持出参结构的版本管理和变更历史记录</li>
            <li>方便开发人员快速复制出参 JSON 用于系统对接</li>
          </ul>
        </a-typography-paragraph>
        
        <a-divider />
        
        <a-typography-title :level="4">二、功能需求</a-typography-title>
        
        <a-typography-title :level="5">2.1 入口</a-typography-title>
        <a-typography-paragraph>
          <ul>
            <li><a-typography-text strong>位置</a-typography-text>：步骤管理页面（/call-scheme/:id/steps）</li>
            <li><a-typography-text strong>触发</a-typography-text>：在页面头部「新建Worker」按钮后面，新增「查看出参列表」按钮</li>
            <li><a-typography-text strong>行为</a-typography-text>：点击后<a-typography-text mark>新开浏览器标签页</a-typography-text>，跳转到出参列表页面</li>
          </ul>
        </a-typography-paragraph>
        
        <a-typography-title :level="5">2.2 出参列表页面</a-typography-title>
        <a-typography-paragraph>
          <a-table :columns="featureColumns" :data-source="featureData" :pagination="false" size="small" bordered />
        </a-typography-paragraph>
        
        <a-typography-title :level="5">2.3 版本管理功能</a-typography-title>
        <a-typography-paragraph>
          <a-typography-text strong>版本号格式</a-typography-text>：<a-typography-text code>vX.Y.Z</a-typography-text>（语义化版本）
          <ul>
            <li><a-typography-text code>X</a-typography-text>：主版本号（重大结构变更，如删除字段）</li>
            <li><a-typography-text code>Y</a-typography-text>：次版本号（新增字段）</li>
            <li><a-typography-text code>Z</a-typography-text>：修订号（描述修改、约束调整等小改动）</li>
          </ul>
        </a-typography-paragraph>
        <a-typography-paragraph>
          <a-typography-text strong>版本记录触发时机</a-typography-text>：当 Worker 的 output_params 字段发生变更时，
          系统自动检测变更内容，要求用户选择版本号升级类型并填写变更说明。
        </a-typography-paragraph>
        
        <a-divider />
        
        <a-typography-title :level="4">三、数据结构设计</a-typography-title>
        
        <a-typography-title :level="5">3.1 新增表：出参版本历史表</a-typography-title>
        <a-typography-paragraph>
          <pre class="code-block"><code>CREATE TABLE `ai_output_params_version` (
  `id` BIGINT(20) NOT NULL AUTO_INCREMENT,
  `scheme_id` BIGINT(20) NOT NULL COMMENT 'AI调用流程ID',
  `version` VARCHAR(20) NOT NULL COMMENT '版本号，如 v1.2.0',
  `snapshot` LONGTEXT NOT NULL COMMENT '该版本的完整出参快照',
  `change_summary` TEXT COMMENT '变更摘要',
  `change_description` VARCHAR(500) COMMENT '变更说明',
  `operator_name` VARCHAR(100) COMMENT '操作人姓名',
  `created_at` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (`id`)
);</code></pre>
        </a-typography-paragraph>
        
        <a-typography-title :level="5">3.2 修改表：AI调用流程表</a-typography-title>
        <a-typography-paragraph>
          新增字段：<a-typography-text code>output_params_version</a-typography-text>（当前版本号）、
          <a-typography-text code>output_params_updated_at</a-typography-text>（最后更新时间）
        </a-typography-paragraph>
        
        <a-divider />
        
        <a-typography-title :level="4">四、API 接口</a-typography-title>
        <a-typography-paragraph>
          <a-table :columns="apiColumns" :data-source="apiData" :pagination="false" size="small" bordered />
        </a-typography-paragraph>
        
        <a-divider />
        
        <a-typography-title :level="4">五、验收标准</a-typography-title>
        <a-typography-paragraph>
          <ol>
            <li>✅ 步骤管理页面有「查看出参列表」按钮</li>
            <li>✅ 点击按钮后新开标签页展示出参列表</li>
            <li>✅ 出参以完整 JSON 格式展示，有语法高亮</li>
            <li>✅ 每个 Worker 出参旁有复制按钮，复制功能正常</li>
            <li>✅ 每个 Worker 有编辑按钮，可跳转到编辑页面</li>
            <li>✅ 页面展示当前版本号和更新时间</li>
            <li>✅ 版本历史弹窗可正常查看历史记录</li>
            <li>✅ 修改出参时可记录版本变更</li>
          </ol>
        </a-typography-paragraph>
        
        <a-divider />
        
        <a-typography-title :level="4">六、后续扩展（可选，本期不做）</a-typography-title>
        <a-typography-paragraph>
          <ul>
            <li>☐ 版本对比功能：选择两个版本对比差异</li>
            <li>☐ 出参校验：运行时校验 AI 返回是否符合出参定义</li>
            <li>☐ 出参文档导出：导出为 Markdown/PDF 格式的接口文档</li>
            <li>☐ 变更通知：出参变更时通知相关人员</li>
          </ul>
        </a-typography-paragraph>
      </a-typography>
    </div>
  </a-modal>
</template>

<script setup>
import { ref } from 'vue';

defineProps({
  visible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:visible']);

// 功能点表格数据
const featureColumns = [
  { title: '功能', dataIndex: 'feature', key: 'feature', width: 150 },
  { title: '说明', dataIndex: 'description', key: 'description' }
];

const featureData = [
  { key: '1', feature: '层级展示', description: '按 步骤 → Worker 层级展示，每个 Worker 显示完整的 output_params JSON' },
  { key: '2', feature: 'JSON 高亮', description: 'JSON 代码语法高亮显示，提升可读性' },
  { key: '3', feature: '复制按钮', description: '每个 Worker 的出参 JSON 旁有复制按钮，一键复制到剪贴板' },
  { key: '4', feature: '编辑跳转', description: '每个 Worker 区块有「编辑」按钮，点击跳转到对应 Worker 的编辑页面' },
  { key: '5', feature: '空状态处理', description: '如果 Worker 没有定义出参，显示"暂未定义出参"' },
  { key: '6', feature: '版本信息', description: '页面顶部显示当前版本号和最后更新时间' }
];

// API接口表格数据
const apiColumns = [
  { title: '接口', dataIndex: 'api', key: 'api', width: 350 },
  { title: '说明', dataIndex: 'description', key: 'description' }
];

const apiData = [
  { key: '1', api: 'GET /api/call-scheme/:schemeId/output-params', description: '获取指定方案的出参列表' },
  { key: '2', api: 'GET /api/call-scheme/:schemeId/output-params/versions', description: '获取版本历史列表' },
  { key: '3', api: 'GET /api/output-params/version/:versionId', description: '获取指定版本的详细快照' },
  { key: '4', api: 'POST /api/call-step/:workerId/output-params', description: '保存出参变更（带版本记录）' }
];

const handleClose = () => {
  emit('update:visible', false);
};
</script>

<style scoped>
.requirement-doc {
  max-height: 70vh;
  overflow-y: auto;
  padding: 0 16px;
}

.code-block {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 16px;
  border-radius: 8px;
  overflow-x: auto;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 12px;
  line-height: 1.5;
}

.requirement-doc :deep(.ant-typography h3) {
  margin-top: 0;
}

.requirement-doc :deep(.ant-typography h4) {
  color: #1890ff;
}

.requirement-doc :deep(.ant-typography h5) {
  color: #666;
}

.requirement-doc :deep(ul),
.requirement-doc :deep(ol) {
  padding-left: 24px;
}

.requirement-doc :deep(li) {
  margin-bottom: 8px;
}

.requirement-doc :deep(.ant-table) {
  margin: 16px 0;
}
</style>

